export const FLIPKART_PLACEMENT_FIELDS = [
  { id: 'HOME_PAGE', label: 'Home Page' },
  { id: 'SEARCH_PAGE_TOP_SLOT', label: 'Top of Search' },
  { id: 'SEARCH_PAGE', label: 'Rest of Search' },
  { id: 'BROWSE_PAGE_TOP_SLOT', label: 'Top of Browse' },
  { id: 'BROWSE_PAGE', label: 'Rest of Browse' },
  { id: 'PRODUCT_PAGE', label: 'Product Page' },
] as const;

export type FlipkartPlacementKey = (typeof FLIPKART_PLACEMENT_FIELDS)[number]['id'];

export type FlipkartPlacementMode =
  | 'set_inr'
  | 'increase_pct'
  | 'decrease_pct'
  | 'increase_inr'
  | 'decrease_inr';

export const FLIPKART_PLACEMENT_MODE_LABELS: Record<FlipkartPlacementMode, string> = {
  set_inr: 'Set value (₹)',
  increase_pct: 'Increase by %',
  decrease_pct: 'Decrease by %',
  increase_inr: 'Increase by ₹',
  decrease_inr: 'Decrease by ₹',
};

export type FlipkartPlacementRowState = {
  mode: FlipkartPlacementMode | '';
  value: string;
};

export type FlipkartPlacementFormState = Record<FlipkartPlacementKey, FlipkartPlacementRowState>;

export type FlipkartPlacementAdjustment = {
  placement: FlipkartPlacementKey;
  mode: FlipkartPlacementMode;
  value: number;
};

export function emptyFlipkartPlacementForm(): FlipkartPlacementFormState {
  return {
    HOME_PAGE: { mode: '', value: '' },
    SEARCH_PAGE_TOP_SLOT: { mode: '', value: '' },
    SEARCH_PAGE: { mode: '', value: '' },
    BROWSE_PAGE_TOP_SLOT: { mode: '', value: '' },
    BROWSE_PAGE: { mode: '', value: '' },
    PRODUCT_PAGE: { mode: '', value: '' },
  };
}

export function parseFlipkartPlacementForm(form: FlipkartPlacementFormState): {
  adjustments: FlipkartPlacementAdjustment[];
  error: string | null;
} {
  const adjustments: FlipkartPlacementAdjustment[] = [];
  for (const field of FLIPKART_PLACEMENT_FIELDS) {
    const row = form[field.id];
    const raw = row.value.trim();
    if (!row.mode && raw === '') continue;
    if (!row.mode) {
      return { adjustments: [], error: `Choose how to adjust ${field.label}` };
    }
    if (raw === '') continue;
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) {
      return { adjustments: [], error: `Enter a valid number for ${field.label}` };
    }
    if (row.mode === 'set_inr' && numeric < 0) {
      return { adjustments: [], error: `${field.label} value must be 0 or greater` };
    }
    if (row.mode !== 'set_inr' && numeric <= 0) {
      return { adjustments: [], error: `${field.label} change must be greater than 0` };
    }
    adjustments.push({ placement: field.id, mode: row.mode, value: numeric });
  }
  return { adjustments, error: null };
}

export type FlipkartPlacementBidView = {
  id: FlipkartPlacementKey;
  label: string;
  bid: number | null;
  minimum: number | null;
};

/** Ad-group fields the placements column reads. */
export type FlipkartPlacementCostSource = {
  cost: number | null;
  floor_price: number | null;
  cost_variation: {
    placements?: { type?: string | null; absoluteCost?: number | null }[];
  } | null;
};

function finiteAmount(value: number | null | undefined): number | null {
  return value != null && Number.isFinite(value) ? value : null;
}

/** All six placement bids. `absoluteCost` wins, including 0; otherwise the ad group cost. */
export function flipkartPlacementBidViews(
  adGroup: FlipkartPlacementCostSource
): FlipkartPlacementBidView[] {
  const byType = new Map<string, number | null>();
  for (const item of adGroup.cost_variation?.placements ?? []) {
    const type = item.type?.trim();
    if (!type) continue;
    byType.set(type, finiteAmount(item.absoluteCost));
  }
  const fallback = finiteAmount(adGroup.cost);
  const floor = finiteAmount(adGroup.floor_price);
  const minimum = floor != null && floor > 0 ? floor : null;

  return FLIPKART_PLACEMENT_FIELDS.map((field) => ({
    id: field.id,
    label: field.label,
    bid: byType.has(field.id) ? (byType.get(field.id) ?? fallback) : fallback,
    minimum,
  }));
}

export function formatFlipkartPlacementToast(result: {
  applied: unknown[];
  skipped: { reason: string }[];
  failed: unknown[];
}): string {
  const applied = result.applied.length;
  const pca = result.skipped.filter((item) => item.reason === 'not_pla').length;
  const otherSkipped = result.skipped.length - pca;
  const failed = result.failed.length;
  const noun = applied === 1 ? 'ad group' : 'ad groups';
  const notes = [`Updated placement bids for ${applied} ${noun}`];
  if (pca) notes.push(`Skipped ${pca} PCA`);
  if (otherSkipped) notes.push(`Skipped ${otherSkipped}`);
  if (failed) notes.push(`${failed} failed`);
  return notes.join('. ');
}
